import React, { useState, useEffect, useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChartBar,
  faUsers,
  faStar,
  faBriefcase,
  faSpinner,
  faExclamationTriangle,
  faBuilding,
  faCheckCircle,
  faTimesCircle,
  faHourglassHalf,
  faUndo,
} from '@fortawesome/free-solid-svg-icons';
import { backend_url } from '../config';
// Assuming you have a separate dashboard CSS or will integrate the conceptual styles provided below
import '../CandidateDashboard.css';

// --- Constants (Copied from SavedCandidates for dependency) ---
const stageColors = {
  'Not Started': '#adb5bd',
  'Resume Screening': '#a9def9',
  'Phone Interview': '#72d5fd',
  'Technical Assessment': '#2ab7ca',
  'On-site Interview': 'var(--coreops-secondary, #00A4CC)',
  'Reference Check': '#0077b6',
  'Offer Extended': '#2a9d8f',
  Hired: 'var(--coreops-success-text, #28a745)',
  Rejected: 'var(--coreops-error-text, #d9534f)',
  'On Hold': '#fdc500',
};

const interviewStages = [
  'Not Started',
  'Resume Screening',
  'Phone Interview',
  'Technical Assessment',
  'On-site Interview',
  'Reference Check',
  'Offer Extended',
  'Hired',
  'Rejected',
  'On Hold',
];
// ----------------------------------------------------------------

function CandidateDashboard() {
  const location = useLocation();
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // --- Data Fetching Logic (Reused from SavedCandidates) ---
  const fetchCandidates = async () => {
    // ... (rest of the fetch logic is the same)
    try {
      setLoading(true);
      setError(null);
      const endpoint = location.pathname.toLowerCase().includes('qualified')
        ? `${backend_url}/qualified-candidates`
        : `${backend_url}/candidates`;
      const response = await fetch(endpoint);

      if (!response.ok) {
        const errorData = await response
          .json()
          .catch(() => ({ message: `HTTP error! status: ${response.status}` }));
        throw new Error(
          errorData.message || `Failed to fetch candidates: ${response.status}`
        );
      }

      const data = await response.json();
      const candidatesArray = (
        Array.isArray(data) ? data : data.candidates || []
      ).map((c) => ({ ...c, similarityScore: c.similarityScore || 0 }));
      setCandidates(candidatesArray);
    } catch (err) {
      console.error('Error fetching candidates:', err);
      setError(err.message || 'Failed to load candidates. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCandidates();
  }, [location.pathname]);
  // ----------------------------------------------------------------

  // --- Dashboard Metrics Calculation & Pie Chart Data Preparation ---
  const dashboardMetrics = useMemo(() => {
    const totalCandidates = candidates.length;
    let totalScore = 0;
    const statusCounts = {};
    const companyCounts = {};
    const domainCounts = {};

    candidates.forEach((c) => {
      const status = c.interviewStatus || 'Not Started';
      statusCounts[status] = (statusCounts[status] || 0) + 1;
      totalScore += c.similarityScore || 0;

      (c.companiesWorked || []).forEach((company) => {
        const name = company.companyName?.trim();
        if (name) companyCounts[name] = (companyCounts[name] || 0) + 1;
      });

      const domain = c.mainDomain?.trim() || 'Unspecified';
      domainCounts[domain] = (domainCounts[domain] || 0) + 1;
    });

    // Structure Status Data for Pie Chart & Legend
    const statusData = interviewStages
      .map((stage) => {
        const count = statusCounts[stage] || 0;
        return {
          stage: stage,
          count: count,
          color: stageColors[stage],
          percentage: totalCandidates > 0 ? (count / totalCandidates) * 100 : 0,
        };
      })
      .filter((item) => item.count > 0); // Only stages with candidates

    const avgScore =
      totalCandidates > 0 ? (totalScore / totalCandidates).toFixed(1) : 0;

    const topCompanies = Object.entries(companyCounts)
      .sort(([, a], [, b]) => b - a)
      .slice(0, 5);

    const topDomains = Object.entries(domainCounts)
      .sort(([, a], [, b]) => b - a)
      .slice(0, 5);

    return {
      totalCandidates,
      statusCounts,
      statusData, // NEW: Structured data for pie chart
      avgScore,
      topCompanies,
      topDomains,
    };
  }, [candidates]);

  // --- Pie Chart CSS Generator ---
  const pieChartStyle = useMemo(() => {
    if (dashboardMetrics.statusData?.length === 0) return {};

    let currentDegree = 0;
    const gradientParts = [];

    // Calculate conic gradient segments
    dashboardMetrics.statusData.forEach((item) => {
      const degree = (item.count / dashboardMetrics.totalCandidates) * 360;
      const nextDegree = currentDegree + degree;

      if (degree > 0) {
        gradientParts.push(
          `${item.color} ${currentDegree}deg ${nextDegree}deg`
        );
        currentDegree = nextDegree;
      }
    });

    return {
      background: `conic-gradient(${gradientParts.join(', ')})`,
    };
  }, [dashboardMetrics.statusData, dashboardMetrics.totalCandidates]);
  // ----------------------------------------------------------------

  const totalCandidates = dashboardMetrics.totalCandidates || 0;

  if (loading) {
    // ... (Loading state remains the same)
    return (
      <div className='coreops-page-container saved-candidates-page'>
        <div className='coreops-message-container loading'>
          <FontAwesomeIcon icon={faSpinner} spin size='3x' />
          <p>Loading Dashboard Data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    // ... (Error state remains the same)
    return (
      <div className='coreops-page-container saved-candidates-page'>
        <div className='coreops-message-container error'>
          <FontAwesomeIcon icon={faExclamationTriangle} size='3x' />
          <p className='error-title'>Error Loading Data</p>
          <p>{error}</p>
          <button
            className='coreops-button coreops-button-primary'
            onClick={fetchCandidates}
          >
            <FontAwesomeIcon icon={faUndo} /> Retry
          </button>
        </div>
      </div>
    );
  }

  if (totalCandidates === 0) {
    // ... (Empty state remains the same)
    return (
      <div className='coreops-page-container saved-candidates-page'>
        <div className='coreops-message-container empty'>
          <FontAwesomeIcon icon={faUsers} size='4x' />
          <p className='empty-title'>Candidate Pipeline Empty</p>
          <p className='empty-subtitle'>
            There is no data to display on the dashboard.
          </p>
          <Link to='/upload' className='coreops-button coreops-button-primary'>
            <FontAwesomeIcon icon={faBriefcase} /> Analyze New Resumes
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className='coreops-page-container dashboard-page'>
      <div className='coreops-content-wrapper'>
        <header className='coreops-main-header results-sub-header'>
          <h1>
            <FontAwesomeIcon icon={faChartBar} /> Candidate Pipeline Dashboard
          </h1>
          <Link
            to='/candidates'
            className='coreops-button coreops-button-secondary'
          >
            <FontAwesomeIcon icon={faUsers} /> View All Candidates Table
          </Link>
        </header>

        {/* --- KPI Cards --- */}
        <div className='dashboard-kpi-grid'>
          <div className='dashboard-card kpi-card total-candidates'>
            <FontAwesomeIcon icon={faUsers} className='kpi-icon' />
            <span className='kpi-value'>{totalCandidates}</span>
            <span className='kpi-label'>Total Candidates</span>
          </div>

          <div className='dashboard-card kpi-card avg-score'>
            <FontAwesomeIcon icon={faStar} className='kpi-icon' />
            <span className='kpi-value'>{dashboardMetrics.avgScore}%</span>
            <span className='kpi-label'>Avg Similarity Score</span>
          </div>

          <div className='dashboard-card kpi-card hired-count'>
            <FontAwesomeIcon icon={faCheckCircle} className='kpi-icon' />
            <span className='kpi-value'>
              {dashboardMetrics.statusCounts.Hired || 0}
            </span>
            <span className='kpi-label'>Total Hired</span>
          </div>

          <div className='dashboard-card kpi-card rejected-count'>
            <FontAwesomeIcon icon={faTimesCircle} className='kpi-icon' />
            <span className='kpi-value'>
              {dashboardMetrics.statusCounts.Rejected || 0}
            </span>
            <span className='kpi-label'>Total Rejected</span>
          </div>

          <div className='dashboard-card kpi-card on-hold-count'>
            <FontAwesomeIcon icon={faHourglassHalf} className='kpi-icon' />
            <span className='kpi-value'>
              {dashboardMetrics.statusCounts['On Hold'] || 0}
            </span>
            <span className='kpi-label'>On Hold</span>
          </div>
        </div>

        {/* --- Charts/Distributions --- */}
        <div className='dashboard-charts-grid pie-layout'>
          {/* Chart 1: Interview Status PIE Chart */}
          <div className='dashboard-card dashboard-chart-card'>
            <h3 className='card-title'>
              <FontAwesomeIcon icon={faChartBar} /> Interview Stage Distribution
            </h3>
            <div className='pie-chart-container'>
              <div className='pie-chart' style={pieChartStyle}>
                {/* Center text for a 'donut' effect */}
                <div className='pie-center-text'>
                  {totalCandidates} <br /> Total
                </div>
              </div>
              <div className='pie-legend'>
                {dashboardMetrics.statusData.map((item) => (
                  <Link
                    to={`/candidates?stage=${item.stage}`}
                    key={item.stage}
                    className='legend-item'
                    title={`${item.stage}: ${
                      item.count
                    } (${item.percentage.toFixed(1)}%)`}
                  >
                    <span
                      className='legend-color'
                      style={{ backgroundColor: item.color }}
                    ></span>
                    <span className='legend-label'>{item.stage}</span>
                    <span className='legend-value'>
                      {item.count} ({item.percentage.toFixed(1)}%)
                    </span>
                  </Link>
                ))}
              </div>
            </div>
            <p className='chart-note'>
              Click on a stage in the legend to view filtered candidates.
            </p>
          </div>

          {/* Chart 2 & 3: Top Companies & Domains (Kept the list view) */}
          <div className='dashboard-chart-side-panel'>
            <div className='dashboard-card dashboard-list-card'>
              <h3 className='card-title'>
                <FontAwesomeIcon icon={faBuilding} /> Top 5 Companies
              </h3>
              {/* ... (Top Companies list rendering as before) */}
              <ul className='list-with-bars'>
                {dashboardMetrics.topCompanies.length > 0 ? (
                  dashboardMetrics.topCompanies.map(([name, count]) => {
                    const maxCompanyCount = Math.max(
                      ...dashboardMetrics.topCompanies.map(([, c]) => c)
                    );
                    const barWidth =
                      maxCompanyCount > 0 ? (count / maxCompanyCount) * 100 : 0;
                    return (
                      <Link
                        to={`/candidates?company=${encodeURIComponent(name)}`}
                        key={name}
                        className='list-item-with-bar'
                      >
                        <span className='item-name' title={name}>
                          {name.substring(0, 25) +
                            (name.length > 25 ? '...' : '')}
                        </span>
                        <div className='item-bar-wrapper'>
                          <div
                            className='item-bar'
                            style={{ width: `${barWidth}%` }}
                          ></div>
                          <span className='item-count'>{count}</span>
                        </div>
                      </Link>
                    );
                  })
                ) : (
                  <p className='no-data-msg'>No company data found.</p>
                )}
              </ul>
            </div>

            <div className='dashboard-card dashboard-list-card'>
              <h3 className='card-title'>
                <FontAwesomeIcon icon={faBriefcase} /> Top 5 Domains
              </h3>
              {/* ... (Top Domains list rendering as before) */}
              <ul className='simple-list'>
                {dashboardMetrics.topDomains.length > 0 ? (
                  dashboardMetrics.topDomains.map(([domain, count]) => (
                    <li key={domain} className='simple-list-item'>
                      <span className='item-name'>{domain}</span>
                      <span className='item-count'>{count}</span>
                    </li>
                  ))
                ) : (
                  <p className='no-data-msg'>No domain data found.</p>
                )}
              </ul>
            </div>
          </div>
        </div>

        <footer className='coreops-footer results-sub-footer'>
          <p>© {new Date().getFullYear()} COREHire - Candidate Dashboard</p>
        </footer>
      </div>
    </div>
  );
}

export default CandidateDashboard;
